import { GenericLazyString } from '@eventbrite/i18n';
import { css } from 'emotion';
import React from 'react';
import Button from '../button/Button';
import Divider from '../divider/Divider';
import IconButton from '../iconButton/IconButton';
import CrossChunky from '../iconography/icons/CrossChunky';

interface IModal {
    onClose: () => void;
    showCloseButton?: boolean;
    children: React.ReactNode;
    title: string | GenericLazyString;
    isShown: boolean;
    onPrimaryAction?: () => void;
    primaryActionLabel: string | GenericLazyString;
    height?: number;
    width?: number;
    footerContent?: React.ReactNode;
    disablePrimaryAction?: boolean;
    hideFooter?: boolean;
}

const Modal = ({
    onClose,
    showCloseButton,
    children,
    title,
    isShown,
    onPrimaryAction = () => undefined,
    primaryActionLabel,
    height,
    width,
    footerContent,
    disablePrimaryAction,
    hideFooter,
}: IModal) => {
    const CloseButton = ({ onClose }: { onClose: () => void }) => {
        let component = null;

        if (onClose) {
            component = (
                <div className="eds-modal__close-button">
                    <IconButton iconType={<CrossChunky />} onClick={onClose} />
                </div>
            );
        }
        return component;
    };

    let footer;

    if (!isShown) {
        return null;
    }

    if (!hideFooter) {
        footer = (
            <>
                <Divider />
                <div
                    className="eds-modal__footer-background"
                    data-testid="modal-footer"
                >
                    <div className="">
                        <nav className="">
                            <div
                                className={css({
                                    float: 'right',
                                    margin: '16px 24px',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, auto);',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                })}
                            >
                                {footerContent && (
                                    <div
                                        data-testid="footer-content"
                                        className={css({
                                            float: 'left',
                                            marginRight: '16px',
                                        })}
                                    >
                                        {footerContent}
                                    </div>
                                )}
                                <Button
                                    data-testid="primary-action-button"
                                    style="fill"
                                    onClick={onPrimaryAction}
                                    disabled={disablePrimaryAction}
                                >
                                    {primaryActionLabel}
                                </Button>
                            </div>
                        </nav>
                    </div>
                </div>
            </>
        );
    }

    return (
        <div className="eds-modal eds-fx--fade-in eds-modal--simple eds-modal--active eds-modal--set-height">
            <div
                className="eds-modal__body eds-fx--grow"
                style={{
                    height: `${height}px`,
                    width: `${width}px`,
                }}
            >
                <div className="eds-collapsible-pane-layout">
                    <div className="eds-collapsible-pane-layout__content">
                        <div className="eds-modal__container">
                            <header className="eds-modal__header eds-align--center-vertical">
                                <div className="eds-modal__title-container eds-modal__title-spacing">
                                    <h2
                                        data-testid="modal-title"
                                        className="eds-text-bl--fixed eds-text--truncate eds-modal__title eds-text-color--ui-800"
                                    >
                                        {title}
                                    </h2>
                                </div>
                                <div className="eds-modal__title-spacing">
                                    <Divider />
                                </div>
                            </header>
                            <main className="eds-modal__main">
                                <div className="eds-modal__content">
                                    <div
                                        data-testid="modal-content"
                                        className="eds-modal__content__children"
                                    >
                                        {children}
                                    </div>
                                    {footer}
                                </div>
                            </main>
                        </div>
                    </div>
                </div>
                {showCloseButton && <CloseButton onClose={onClose} />}
            </div>
        </div>
    );
};

export default Modal;
